import React, { useState, useCallback, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";

const StatusBadge = ({ status }) => {
  const styles = {
    pending:
      "bg-amber-100  dark:bg-amber-900/40  text-amber-700  dark:text-amber-300",
    approved:
      "bg-green-100  dark:bg-green-900/40  text-green-700  dark:text-green-300",
    rejected:
      "bg-red-100    dark:bg-red-900/40    text-red-700    dark:text-red-300",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${styles[status] ?? ""}`}
    >
      {status}
    </span>
  );
};

// Cheap heuristic: exact match (normalized) is "confirmed", anything else
// gets flagged for the admin to eyeball rather than silently trusted.
const normalizeName = (s) =>
  (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const namesRoughlyMatch = (a, b) => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  // allow "First Last" vs "Last, First" or a middle name being dropped
  const aParts = na.split(" ");
  const bParts = nb.split(" ");
  return (
    aParts.every((p) => nb.includes(p)) || bParts.every((p) => na.includes(p))
  );
};

const PlayerClaimsPanel = ({ onResolved }) => {
  const { adminId } = useAdmin();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState({});
  const [reasonInput, setReasonInput] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from("player_claims")
      .select(
        `
                id, status, requested_at, reviewed_at, rejection_reason,
                profile:profiles!player_claims_profile_id_fkey(id, name, email),
                player:players(id, name, season:seasons(name), team:teams(name))
            `,
      )
      .order("requested_at", { ascending: false });

    if (filter !== "all") query.eq("status", filter);

    const { data, error } = await query;
    if (!error) setClaims(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const handleDecision = async (claim, decision) => {
    setProcessing((prev) => ({ ...prev, [claim.id]: true }));
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action:
            decision === "approved"
              ? "approvePlayerClaim"
              : "rejectPlayerClaim",
          adminId,
          payload: {
            claimId: claim.id,
            reason:
              decision === "rejected"
                ? reasonInput[claim.id] || null
                : undefined,
          },
        },
      });
      if (error) throw error;
      await loadClaims();
      onResolved?.();
      setExpandedId(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing((prev) => ({ ...prev, [claim.id]: false }));
    }
  };

  const pendingCount = claims.filter((c) => c.status === "pending").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Player Claims
          </h2>
          {pendingCount > 0 && (
            <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>

        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
                filter === f
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-12">
          Loading claims…
        </p>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <div className="text-4xl mb-3">🏀</div>
          <p className="font-medium">
            No {filter !== "all" ? filter : ""} claims
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {claims.map((claim) => {
            const isExpanded = expandedId === claim.id;
            const isProcessing = !!processing[claim.id];
            const matches = namesRoughlyMatch(
              claim.profile?.name,
              claim.player?.name,
            );

            return (
              <div
                key={claim.id}
                className="border dark:border-gray-700 rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm"
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : claim.id)}
                  className="w-full text-left px-5 py-4 flex flex-wrap items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                >
                  <StatusBadge status={claim.status} />

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white leading-snug">
                      {claim.profile?.name || "Unknown user"}
                      <span className="text-gray-400 font-normal mx-1.5">
                        →
                      </span>
                      {claim.player?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {claim.player?.season?.name} · {claim.player?.team?.name}
                      {" · "}
                      requested{" "}
                      {new Date(claim.requested_at).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric" },
                      )}
                    </p>
                  </div>

                  {!matches && claim.status === "pending" && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 shrink-0">
                      ⚠ Name mismatch
                    </span>
                  )}

                  <span
                    className={`text-gray-400 transition-transform duration-200 text-xs mt-0.5 ${isExpanded ? "rotate-180" : ""}`}
                  >
                    ▼
                  </span>
                </button>

                {isExpanded && (
                  <div className="border-t dark:border-gray-700 px-5 py-5 space-y-5">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                          Claiming account
                        </p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {claim.profile?.name}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          {claim.profile?.email}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                          Historical record
                        </p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {claim.player?.name}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          {claim.player?.season?.name} ·{" "}
                          {claim.player?.team?.name}
                        </p>
                      </div>
                    </div>

                    {!matches && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Name on the account doesn't closely match the name on
                        the historical record — worth confirming with the player
                        before approving.
                      </p>
                    )}

                    {claim.status === "pending" && (
                      <div className="space-y-3">
                        <textarea
                          placeholder="Rejection reason (optional — shown only if you reject)"
                          value={reasonInput[claim.id] ?? ""}
                          onChange={(e) =>
                            setReasonInput((prev) => ({
                              ...prev,
                              [claim.id]: e.target.value,
                            }))
                          }
                          rows={2}
                          className="w-full border dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm
                                                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                               placeholder-gray-400 focus:outline-none focus:border-orange-500 resize-none"
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleDecision(claim, "rejected")}
                            disabled={isProcessing}
                            className="flex-1 py-2.5 text-sm font-semibold rounded-xl border-2 border-red-300 dark:border-red-800
                                                                   text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20
                                                                   disabled:opacity-50 transition"
                          >
                            ✗ Reject
                          </button>
                          <button
                            onClick={() => handleDecision(claim, "approved")}
                            disabled={isProcessing}
                            className="flex-1 py-2.5 text-sm font-semibold rounded-xl
                                                                   bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400
                                                                   text-white transition"
                          >
                            {isProcessing ? "Saving…" : "✓ Approve & Link"}
                          </button>
                        </div>
                      </div>
                    )}

                    {claim.status !== "pending" && claim.rejection_reason && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        <span className="font-semibold text-gray-700 dark:text-gray-200">
                          Rejection reason:{" "}
                        </span>
                        {claim.rejection_reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerClaimsPanel;
